import React, { useContext, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { OrderList } from 'primereact/orderlist';
import { MultiSelect } from 'primereact/multiselect';
import { Avatar } from 'primereact/avatar';
import { deleteRequest, getRequest, postRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { CrmAcquisitionIcon, CrmAnnouncementIcon, CrmQuoteRequestIcon, ServiceSettingIcon } from '../svg/svgIcons';

const CRM_TYPES = Object.freeze([
  { key: 'notice', icon: CrmAnnouncementIcon, labelKey: 'CustomerLevels.Type.notice' },
  { key: 'inquiry', icon: CrmQuoteRequestIcon, labelKey: 'CustomerLevels.Type.inquiry' },
  { key: 'acquisition', icon: CrmAcquisitionIcon, labelKey: 'CustomerLevels.Type.acquisition' },
]);

const TASK_KEYS = Object.freeze({
  creation: 'ServiceUsersSettings.Task.Creation',
  management: 'ServiceUsersSettings.Task.Management',
  approval: 'ServiceUsersSettings.Task.Approval',
  inspection: 'ServiceUsersSettings.Task.Inspection',
  notification: 'ServiceUsersSettings.Task.Notification',
});

/** Zladené s BE crmLevelTypeAliasesForTicketType (notice/announcement, inquiry/quote_request). */
const CRM_LEVEL_TYPE_ALIASES = Object.freeze({
  notice: ['notice', 'announcement'],
  inquiry: ['inquiry', 'quote_request'],
  acquisition: ['acquisition'],
});

const NEXT_STEP_MARKER_KEY = 'CustomerLevels.Marker.NextStep';

const INSPECTION_TASK_KEYS = Object.freeze({
  inquiry: 'CustomerLevels.Task.Inspection.inquiry',
  acquisition: 'CustomerLevels.Task.Inspection.acquisition',
});

/** Parita ServiceLevelController – poradie úrovní v rámci typu. */
const CRM_LEVEL_TASK_ORDER = Object.freeze({
  creation: 0,
  management: 1,
  approval: 2,
  execution: 3,
  inspection: 4,
  notification: 5,
});

function levelMatchesType(level, type) {
  const aliases = CRM_LEVEL_TYPE_ALIASES[type] || [type];
  return aliases.includes(String(level?.type ?? ''));
}

export default function CustomerLevelsSettings() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { toast, getUserColor, getUserInitials, setDialogText, setVisible } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);
  const submitRef = useRef(null);
  const [selectedType, setSelectedType] = useState('notice');
  const [showAddLevel, setShowAddLevel] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [changeOrderOfLevel, setChangeOrderOfLevel] = useState(false);
  const [orderedLevels, setOrderedLevels] = useState([]);

  const refreshData = async () => {
    const [levelsResp, levelUsersResp] = await Promise.all([
      getRequest('/api/crm_levels'),
      getRequest('/api/crm_level_user_settings'),
    ]);
    dispatch({ type: 'load_crm_levels', newCrmLevels: Array.isArray(levelsResp?.data) ? levelsResp.data : [] });
    dispatch({
      type: 'load_crm_level_user_settings',
      newCrmLevelUserSettings: Array.isArray(levelUsersResp?.data) ? levelUsersResp.data : [],
    });
  };

  useEffect(() => {
    if (!Array.isArray(state?.crm_levels) || state.crm_levels.length === 0) {
      refreshData().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const levels = Array.isArray(state?.crm_levels) ? state.crm_levels : [];
  const levelUsers = Array.isArray(state?.crm_level_user_settings) ? state.crm_level_user_settings : [];

  const formikUser = useFormik({
    initialValues: { crm_level_id: '', users: [], departments: [], user_department: '' },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      try {
        const payload = new FormData();
        payload.append('crm_level_id', values.crm_level_id);
        payload.append('user_department', values.user_department);
        payload.append('users', (values.users || []).map((x) => x.id).join(','));
        payload.append('departments', (values.departments || []).map((x) => x.id).join(','));
        await postRequest('/api/crm_level_user_settings', payload, true);
        await refreshData();
        setShowAddUser(false);
        formikUser.resetForm();
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('ServiceUsersSettings.UserAddedSuccess'), life: 2500 });
      } catch {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('ServiceTask.ActionFailed'), life: 3000 });
      } finally {
        setVisible?.(false);
      }
    },
  });

  const formikLevel = useFormik({
    initialValues: { name: '', type: 'notice', task: '', active: true },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!String(values.name || '').trim()) errors.name = t('Common.Required');
      if (!String(values.task || '').trim()) errors.task = t('Common.Required');
      return errors;
    },
    onSubmit: async (values) => {
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      try {
        await postRequest('/api/crm_levels', {
          name: String(values.name || '').trim(),
          type: values.type,
          task: values.task,
          active: true,
        }, true);
        await refreshData();
        setShowAddLevel(false);
        setChangeOrderOfLevel(false);
        formikLevel.resetForm();
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('ServiceUsersSettings.LevelAddedSuccess'), life: 2500 });
      } catch {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('CustomerLevels.Toast.SaveFailed'), life: 3000 });
      } finally {
        setVisible?.(false);
      }
    },
  });

  const acceptDeleteLevel = async (id) => {
    try {
      await deleteRequest(`/api/crm_levels/${id}`);
      await refreshData();
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('ServiceUsersSettings.LevelDeletedSuccess'), life: 2500 });
    } catch {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('ServiceUsersSettings.LevelDeleteFailed'), life: 2500 });
    }
  };

  const deleteLevel = (id) => {
    confirmDialog({
      message: t('ServiceUsersSettings.ConfirmDeleteLevel'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: () => acceptDeleteLevel(id),
    });
  };

  const acceptDeleteSetting = async (id) => {
    try {
      await deleteRequest(`/api/crm_level_user_settings/${id}`);
      await refreshData();
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('ServiceUsersSettings.SettingDeletedSuccess'), life: 2500 });
    } catch {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('ServiceUsersSettings.DeleteSettingFailed'), life: 2500 });
    }
  };

  const deleteSetting = (id) => {
    confirmDialog({
      message: t('ServiceUsersSettings.ConfirmDeleteSetting'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: () => acceptDeleteSetting(id),
    });
  };

  /** Parita ServiceLevelController::index – creation → management → approval → inspection → notification. */
  const levelsForType = (type) => levels
    .filter((l) => levelMatchesType(l, type))
    .slice()
    .sort((a, b) => {
      const ta = CRM_LEVEL_TASK_ORDER[String(a?.task || '').toLowerCase()] ?? 99;
      const tb = CRM_LEVEL_TASK_ORDER[String(b?.task || '').toLowerCase()] ?? 99;
      if (ta !== tb) return ta - tb;
      const ra = Number(a?.rating ?? a?.sort_order ?? 0);
      const rb = Number(b?.rating ?? b?.sort_order ?? 0);
      return ra - rb || Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });

  const openReorder = (type) => {
    setSelectedType(type);
    setOrderedLevels(
      levelsForType(type)
        .filter((l) => String(l.task) === 'approval')
        .slice()
        .sort((a, b) => Number(a?.rating ?? a?.sort_order ?? 0) - Number(b?.rating ?? b?.sort_order ?? 0)),
    );
    setChangeOrderOfLevel(true);
  };

  const getExecutionTitle = (type) => {
    if (type === 'inquiry' || type === 'acquisition') {
      return t(NEXT_STEP_MARKER_KEY);
    }
    return t('ServiceUsersSettings.Execution');
  };

  const setRatings = async () => {
    setDialogText?.(t('Common.Saving'));
    setVisible?.(true);
    try {
      const formData = new FormData();
      formData.append('levels', orderedLevels.map((level) => level.id).join(','));
      await postRequest('/api/crm_levels/set_ratings', formData, true);
      await refreshData();
      setChangeOrderOfLevel(false);
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('ServiceUsersSettings.OrderSaved'), life: 2500 });
    } catch {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('ServiceTask.ActionFailed'), life: 3000 });
      setChangeOrderOfLevel(false);
    } finally {
      setVisible?.(false);
    }
  };

  const itemTemplate = (item) => (
    <div className="flex flex-wrap p-2 align-items-center gap-3">
      <div className="flex-1 flex flex-column gap-2 xl:mr-8">
        <span className="font-bold">{item.name}</span>
      </div>
      <span className="font-bold text-900">{item.rating ?? item.sort_order}</span>
    </div>
  );

  const getTask = (task, type) => {
    const taskLower = String(task || '').toLowerCase();
    if (taskLower === 'inspection' && type && INSPECTION_TASK_KEYS[type]) {
      return t(INSPECTION_TASK_KEYS[type]);
    }
    const key = TASK_KEYS[taskLower];
    return key ? t(key) : String(task || '');
  };

  const removeDiacritics = (str) => String(str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const renderLevelsForType = (type) => {
    const data = levelsForType(type);
    if (!data.length) return null;

    const showExecutionMarker = type !== 'notice';

    const approvalIndexes = [];
    const inspectionIndexes = [];
    const notificationIndexes = [];

    data.forEach((l, i) => {
      if (String(l.task) === 'approval') approvalIndexes.push(i);
      if (String(l.task) === 'inspection') inspectionIndexes.push(i);
      if (String(l.task) === 'notification') notificationIndexes.push(i);
    });

    const lastApprovalIndex = approvalIndexes.length ? approvalIndexes[approvalIndexes.length - 1] : -1;
    const firstInspectionIndex = inspectionIndexes.length ? inspectionIndexes[0] : -1;
    const firstNotificationIndex = notificationIndexes.length ? notificationIndexes[0] : -1;

    let executionInsertIndex = null;
    if (showExecutionMarker) {
      if (approvalIndexes.length && inspectionIndexes.length) {
        executionInsertIndex = lastApprovalIndex + 1;
      } else if (approvalIndexes.length) {
        executionInsertIndex = lastApprovalIndex + 1;
      } else if (inspectionIndexes.length) {
        executionInsertIndex = firstInspectionIndex;
      } else if (notificationIndexes.length) {
        executionInsertIndex = firstNotificationIndex;
      } else {
        executionInsertIndex = data.length;
      }
    }

    const lineStyle = { border: 0, borderTop: '2px solid #d4d4d8' };

    const sequence = [];
    for (let i = 0; i <= data.length; i += 1) {
      if (showExecutionMarker && executionInsertIndex === i) {
        sequence.push({ kind: 'execution', key: `${type}-execution-${i}` });
      }
      if (i < data.length) {
        sequence.push({ kind: 'level', level: data[i], key: data[i].id });
      }
    }

    const elements = [];

    const renderLevelBlock = (level) => (
      <React.Fragment key={level.id}>
        <div className="flex justify-between mb-2">
          <div>
            <label className="font-bold mb-1 text-lg mr-1">{level?.name}</label>
            <label className="font-normal mb-1 text-lg">{` - ${getTask(level?.task, type)}`}</label>
          </div>
          <button type="button" onClick={() => deleteLevel(level?.id)}>
            <i className="pi pi-trash px-1 py-1" style={{ fontSize: '1.5rem' }} />
          </button>
        </div>

        <div className="h-auto overflow-y-auto overflow-x-hidden flex flex-col w-full mb-5">
          {levelUsers
            .filter((setting) => Number(setting.crm_level_id) === Number(level.id))
            .map((setting) => (
              <div key={setting.id} className="flex justify-between items-start gap-10 text-gray-800 mb-2">
                <div className="flex flex-row">
                  <Avatar
                    className="p-overlay-badge"
                    title={setting?.user?.employee?.name || setting?.user?.username || setting?.department?.name}
                    label={getUserInitials(setting?.user?.employee?.name || setting?.user?.username || setting?.department?.name)}
                    shape="circle"
                    style={{
                      backgroundColor: getUserColor(setting?.user?.username || setting?.department?.name),
                      color: '#ffffff',
                      marginRight: '5px',
                    }}
                  />
                  <div className="flex justify-left flex-col justify-center">
                    <span>{setting?.user?.employee?.name || setting?.user?.username || setting?.department?.name}</span>
                  </div>
                </div>
                <button type="button" onClick={() => deleteSetting(setting.id)}>
                  <i className="pi pi-times-circle cursor-pointer" title={t('Common.Delete')} style={{ fontSize: '1.5rem' }} />
                </button>
              </div>
            ))}
          <div className="text-gray-800">
            <button
              type="button"
              onClick={() => {
                formikUser.resetForm();
                formikUser.setFieldValue('crm_level_id', level?.id);
                setShowAddUser(true);
              }}
              className="flex items-center gap-2 disabled:opacity-25"
            >
              <i className="pi pi-plus mr-2 px-1" style={{ fontSize: '1.5rem' }} />
              {t('ServiceUsersSettings.AddDepartmentOrUser')}
            </button>
          </div>
        </div>
      </React.Fragment>
    );

    if (sequence.length) {
      elements.push(
        <hr key={`${type}-sep-0`} style={lineStyle} className="mb-4" />,
      );
    }

    sequence.forEach((item, idx) => {
      if (idx > 0) {
        elements.push(
          <hr key={`${type}-sep-${idx}`} style={lineStyle} className="my-4" />,
        );
      }

      if (item.kind === 'execution') {
        elements.push(
          <div key={item.key} className="text-lg font-semibold tracking-wide">
            {getExecutionTitle(type)}
          </div>,
        );
      } else {
        elements.push(renderLevelBlock(item.level));
      }
    });

    return elements;
  };

  return (
    <>
      <div className="h-[calc(100vh-136px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
        <div className="xl:columns-3 lg:columns-2 gap-4 space-y-4 m-4">
          {CRM_TYPES.map((typeDef) => {
            const Icon = typeDef.icon;
            return (
              <div key={typeDef.key} className="w-auto p-6 bg-white rounded-md shadow-md bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 break-inside-avoid">
                <div className="flex flex-row justify-between items-center mb-8">
                  <h1 className="flex flex-row items-center text-3xl font-semibold text-left text-zinc-900">
                    <Icon width={10} height={10} />
                    {t(typeDef.labelKey)}
                  </h1>
                  <button type="button" onClick={() => openReorder(typeDef.key)}>
                    <ServiceSettingIcon />
                  </button>
                </div>
                <div className="text-gray-800 mb-5">
                  <button
                    type="button"
                    onClick={() => {
                      formikLevel.resetForm();
                      formikLevel.setFieldValue('type', typeDef.key);
                      formikLevel.setFieldValue('task', '');
                      setShowAddLevel(true);
                    }}
                    className="flex items-center gap-2 disabled:opacity-25"
                  >
                    <i className="pi pi-plus mr-2 px-1" style={{ fontSize: '1.5rem' }} />
                    {t('ServiceUsersSettings.AddApprovalLevel')}
                  </button>
                </div>
                {renderLevelsForType(typeDef.key)}
              </div>
            );
          })}
        </div>
      </div>

      <button className="hidden" type="submit" ref={submitRef} />

      <Dialog
        header={t('ServiceUsersSettings.AddDepartmentOrUserDialog')}
        visible={showAddUser}
        style={{ width: '50vw' }}
        onHide={() => {
          setShowAddUser(false);
          formikUser.resetForm();
        }}
      >
        <form onSubmit={formikUser.handleSubmit}>
          <div className="w-auto flex flex-col">
            <select
              name="user_department"
              onChange={(e) => {
                formikUser.setFieldValue('user_department', e.target.value);
                formikUser.setFieldValue('users', []);
                formikUser.setFieldValue('departments', []);
              }}
              value={formikUser.values.user_department}
              className="bg-white block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            >
              <option value="">{t('Common.SelectOption')}</option>
              <option value="department">{t('Common.Department')}</option>
              <option value="user">{t('Common.User')}</option>
            </select>

            {formikUser.values.user_department === 'department' && (
              <MultiSelect
                onChange={(e) => formikUser.setFieldValue('departments', e.target.value)}
                value={formikUser.values.departments}
                options={(state?.departments || []).map((d) => ({ name: d.name, id: d.id }))}
                optionLabel="name"
                filter
                placeholder={t('ServiceTaskDetail.SelectDepartments')}
                className="w-full px-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
              />
            )}

            {formikUser.values.user_department === 'user' && (
              <MultiSelect
                onChange={(e) => formikUser.setFieldValue('users', e.target.value)}
                value={formikUser.values.users}
                options={(state?.users || []).map((u) => ({
                  name: u?.employee?.name || u?.username,
                  id: u.id,
                  searchLabel: removeDiacritics(u?.employee?.name || u?.username).toLowerCase(),
                }))}
                optionLabel="name"
                filter
                filterBy="searchLabel"
                filterMatchMode="contains"
                filterPlaceholder={t('Common.Search')}
                placeholder={t('ServiceUsersSettings.SelectUsers')}
                className="w-full px-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
              />
            )}
          </div>
          <button
            type="submit"
            disabled={formikUser.values.users?.length < 1 && formikUser.values.departments?.length < 1}
            className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25"
          >
            {t('Common.Save')}
          </button>
        </form>
      </Dialog>

      <Dialog
        header={t('ServiceUsersSettings.AddApprovalLevelDialog')}
        visible={showAddLevel}
        style={{ width: '50vw' }}
        onHide={() => {
          setShowAddLevel(false);
          formikLevel.resetForm();
        }}
      >
        <form onSubmit={formikLevel.handleSubmit}>
          <div className="w-auto flex flex-col">
            <label>{t('ServiceUsersSettings.LevelName')}</label>
            <input
              name="name"
              onChange={formikLevel.handleChange}
              value={formikLevel.values.name}
              className="block w-full mb-2 px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
          </div>
          <div className="w-auto flex flex-col">
            <label>{t('ServiceUsersSettings.LevelTask')}</label>
            <select
              name="task"
              onChange={formikLevel.handleChange}
              value={formikLevel.values.task}
              className="bg-white block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            >
              <option value="">{t('Common.SelectOption')}</option>
              <option
                value="creation"
                hidden={levelsForType(formikLevel.values.type).some((l) => String(l.task) === 'creation')}
              >
                {t('ServiceUsersSettings.Task.Creation')}
              </option>
              <option
                value="management"
                hidden={levelsForType(formikLevel.values.type).some((l) => String(l.task) === 'management')}
              >
                {t('ServiceUsersSettings.Task.Management')}
              </option>
              <option value="approval">{t('ServiceUsersSettings.Task.Approval')}</option>
              <option value="inspection" hidden={formikLevel.values.type === 'notice'}>
                {formikLevel.values.type === 'acquisition'
                  ? t(INSPECTION_TASK_KEYS.acquisition)
                  : formikLevel.values.type === 'inquiry'
                    ? t(INSPECTION_TASK_KEYS.inquiry)
                    : t('ServiceUsersSettings.Task.Inspection')}
              </option>
              <option
                value="notification"
                hidden={levelsForType(formikLevel.values.type).some((l) => String(l.task) === 'notification')}
              >
                {t('ServiceUsersSettings.Task.Notification')}
              </option>
            </select>
          </div>
          <button
            type="submit"
            disabled={formikLevel.values.name === '' || formikLevel.values.task === ''}
            className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25"
          >
            {t('Common.Save')}
          </button>
        </form>
      </Dialog>

      <Dialog header={t('ServiceUsersSettings.ChangeLevelOrderDialog')} visible={changeOrderOfLevel} style={{ width: '50vw' }} onHide={() => setChangeOrderOfLevel(false)}>
        <div className="card flex flex-col justify-content-center">
          <OrderList
            className="w-full"
            dragdrop
            dataKey="id"
            value={orderedLevels}
            onChange={(e) => setOrderedLevels(e.value)}
            itemTemplate={itemTemplate}
            header={`${t('ServiceUsersSettings.ApprovalLevelsHeader')} - ${t(CRM_TYPES.find((x) => x.key === selectedType)?.labelKey || '')}`}
          />
        </div>
        <button type="button" onClick={setRatings} className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25">
          {t('Common.Save')}
        </button>
      </Dialog>
    </>
  );
}
